import { Button, Dropdown, Popover, Tooltip, Typography, theme } from 'antd'
import {
  DownOutlined,
  EditOutlined,
  ExpandAltOutlined,
  LoadingOutlined,
  MailOutlined,
  MessageOutlined,
  ShrinkOutlined,
  StarOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import {
  ChatwootReplyRecipients,
  type ReplyRecipients,
} from './ChatwootReplyRecipients'
import { normalizeRecipients } from '../../lib/shared/chatwootRecipients'
import styles from './ChatwootInbox.module.less'

export type ComposerMode = 'reply' | 'note'

// What the Cleo chip says, and whether it does anything when clicked. Derived by
// the composer from the suggestion status plus its own view state, because two of
// these ('draft', 'waiting') are about which body is on screen rather than about
// what Cleo is doing.
//
//   draft      — a suggestion has the composer body; the chip is just a label
//   waiting    — a ready draft is held back so it can't take the field from a
//                half-written reply; clicking hands the field over
//   generating — Cleo is working, but the operator is writing so the card is not
//                on screen to say so
//   generate   — nothing to show and a customer message awaits a reply
//   noReply    — Cleo declined; the reason is the tooltip
//   error      — a generation attempt failed; clicking retries
export type CleoChipState =
  | 'draft'
  | 'waiting'
  | 'generating'
  | 'generate'
  // Nothing is waiting to be answered, so the advisor tells Cleo what to write.
  | 'compose'
  | 'noReply'
  | 'error'

interface ChatwootComposerHeaderProps {
  mode: ComposerMode
  onModeChange: (next: ComposerMode) => void
  // True on email threads. Chat/widget threads have no addresses to show, so the
  // recipient trigger is absent and the channel reads "Chat".
  isEmailConversation: boolean
  // Tints the bottom border to match whichever body is showing.
  tone: 'reply' | 'note' | 'cleo'
  recipients: ReplyRecipients
  onRecipientsChange: (next: ReplyRecipients) => void
  // True when To is empty on a thread that had a default — the send is blocked,
  // and with the popover closed the trigger has to carry that.
  missingRecipient: boolean
  submitting: boolean
  companyUid?: string | null
  cleoChip: CleoChipState | null
  onCleoChipClick: () => void
  // The expand/collapse control for the box (CL-2886). Null hides it — the body
  // fits the default height, so there is nothing to expand.
  expand?: { expanded: boolean; onToggle: () => void } | null
}

// The composer's single header row.
//
// It replaces three stacked blocks — the Reply/Note tabs, the "reply goes to"
// line and Cleo's status sentence — with one 32px strip. That is the whole point:
// those three cost ~120px of a 200px box, which left barely two lines to type in
// once Cc/Bcc were open or Cleo had something to say. Nothing here is new
// functionality; the recipient editor and the Cleo actions are the same ones,
// reached differently (CL-2214).
export function ChatwootComposerHeader({
  mode,
  onModeChange,
  isEmailConversation,
  tone,
  recipients,
  onRecipientsChange,
  missingRecipient,
  submitting,
  companyUid,
  cleoChip,
  onCleoChipClick,
  expand,
}: ChatwootComposerHeaderProps) {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const [recipientsOpen, setRecipientsOpen] = useState(false)

  // antd renders menu-item icons at 12px, which reads as a smudge next to the
  // 14px label. Set inline so no stylesheet specificity contest is possible.
  const itemIcon = { fontSize: 15 }

  const isNote = mode === 'note'
  const channelLabel = isNote
    ? t('frontend:chatwoot.note.private')
    : isEmailConversation
      ? t('frontend:chatwoot.composer.channelEmail')
      : t('frontend:chatwoot.composer.channelChat')

  const headerClassName = [
    styles.composerHeader,
    tone === 'note'
      ? styles.composerHeaderNote
      : tone === 'cleo'
        ? styles.composerHeaderCleo
        : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={headerClassName}>
      <Dropdown
        trigger={['click']}
        disabled={submitting}
        rootClassName={styles.composerChannelMenu}
        menu={{
          selectable: true,
          selectedKeys: [mode],
          items: [
            {
              key: 'reply',
              icon: isEmailConversation ? (
                <MailOutlined style={itemIcon} />
              ) : (
                <MessageOutlined style={itemIcon} />
              ),
              label: isEmailConversation
                ? t('frontend:chatwoot.composer.channelReplyEmail')
                : t('frontend:chatwoot.composer.channelReplyChat'),
            },
            {
              key: 'note',
              icon: <EditOutlined style={itemIcon} />,
              label: t('frontend:chatwoot.note.private'),
            },
          ],
          onClick: ({ key }) => {
            // Switching channel closes the recipient editor: it belongs to the
            // email reply, and leaving it floating over a note would be lying
            // about where that note goes.
            setRecipientsOpen(false)
            onModeChange(key as ComposerMode)
          },
        }}
      >
        <Button
          type="text"
          size="small"
          className={styles.composerChannel}
          // Note mode's amber accent, matching the yellow box, inline because antd injects its text-button
          // colour at render time — later than this stylesheet — and wins the
          // single-class tie, which silently left the trigger default-black.
          style={isNote ? { color: token.gold9, fontWeight: 600 } : undefined}
          aria-label={t('frontend:chatwoot.composer.channelAria')}
        >
          {isNote ? (
            <EditOutlined />
          ) : isEmailConversation ? (
            <MailOutlined />
          ) : (
            <MessageOutlined />
          )}
          {channelLabel}
          <DownOutlined style={{ fontSize: 11 }} />
        </Button>
      </Dropdown>

      {!isNote && isEmailConversation ? (
        <RecipientTrigger
          recipients={recipients}
          onRecipientsChange={onRecipientsChange}
          missingRecipient={missingRecipient}
          submitting={submitting}
          companyUid={companyUid}
          open={recipientsOpen}
          onOpenChange={setRecipientsOpen}
        />
      ) : (
        <span className={styles.composerHeaderSpacer} />
      )}

      {cleoChip && !isNote && (
        <CleoChip state={cleoChip} onClick={onCleoChipClick} />
      )}

      {expand && (
        <Tooltip
          title={
            expand.expanded
              ? t('frontend:chatwoot.composer.collapse')
              : t('frontend:chatwoot.composer.expand')
          }
        >
          <Button
            type="text"
            size="small"
            className={styles.composerExpandToggle}
            icon={expand.expanded ? <ShrinkOutlined /> : <ExpandAltOutlined />}
            aria-label={
              expand.expanded
                ? t('frontend:chatwoot.composer.collapse')
                : t('frontend:chatwoot.composer.expand')
            }
            aria-expanded={expand.expanded}
            onClick={expand.onToggle}
          />
        </Tooltip>
      )}
    </div>
  )
}

// The recipient trigger and the popover holding the editor.
//
// The editor floats ABOVE the composer rather than opening inside it, so the box
// keeps its height and the caret never jumps — the reason 2b was chosen over an
// expanding header.
function RecipientTrigger({
  recipients,
  onRecipientsChange,
  missingRecipient,
  submitting,
  companyUid,
  open,
  onOpenChange,
}: {
  recipients: ReplyRecipients
  onRecipientsChange: (next: ReplyRecipients) => void
  missingRecipient: boolean
  submitting: boolean
  companyUid?: string | null
  open: boolean
  onOpenChange: (next: boolean) => void
}) {
  const { t } = useTranslation()

  const to = normalizeRecipients(recipients.to)
  const copies = normalizeRecipients([...recipients.cc, ...recipients.bcc])
  // The first address is what the trigger shows; the rest are a count, with the
  // whole distribution list in the tooltip so nothing is hidden outright.
  const primary = to[0] ?? t('frontend:chatwoot.email.noRecipient')
  const extraTo = to.length - 1

  const fullList = [
    `${t('frontend:chatwoot.email.to')}: ${to.join(', ') || '—'}`,
    recipients.cc.length > 0 &&
      `${t('frontend:chatwoot.email.cc')}: ${normalizeRecipients(recipients.cc).join(', ')}`,
    recipients.bcc.length > 0 &&
      `${t('frontend:chatwoot.email.bcc')}: ${normalizeRecipients(recipients.bcc).join(', ')}`,
  ]
    .filter(Boolean)
    .join('\n')

  return (
    <Popover
      open={open}
      onOpenChange={onOpenChange}
      trigger={['click']}
      placement="topLeft"
      // Above the composer, so opening it never resizes the box.
      title={
        <div className={styles.composerRecipientsTitle}>
          <Typography.Text strong>
            {t('frontend:chatwoot.email.recipients')}
          </Typography.Text>
          <Button type="link" size="small" onClick={() => onOpenChange(false)}>
            {t('frontend:chatwoot.email.recipientsDone')}
          </Button>
        </div>
      }
      content={
        <ChatwootReplyRecipients
          value={recipients}
          onChange={onRecipientsChange}
          disabled={submitting}
          missingRecipient={missingRecipient}
          companyUid={companyUid}
        />
      }
    >
      <button
        type="button"
        className={[
          styles.composerRecipientTrigger,
          missingRecipient ? styles.composerRecipientTriggerMissing : '',
        ]
          .filter(Boolean)
          .join(' ')}
        title={fullList}
        aria-label={t('frontend:chatwoot.email.editRecipients')}
        aria-expanded={open}
      >
        <span className={styles.composerRecipientTriggerAddress}>
          {primary}
        </span>
        {extraTo > 0 && (
          <span className={styles.composerRecipientTriggerCount}>
            {t('frontend:chatwoot.email.moreRecipients', { count: extraTo })}
          </span>
        )}
        {copies.length > 0 && (
          <span className={styles.composerRecipientTriggerCount}>
            {t('frontend:chatwoot.email.copyCount', { count: copies.length })}
          </span>
        )}
        <DownOutlined style={{ fontSize: 10, flex: 'none' }} />
      </button>
    </Popover>
  )
}

// Cleo's status and its action in one control. The sentence this replaced was two
// lines tall and permanently parked above the reply field; it is now the tooltip.
function CleoChip({
  state,
  onClick,
}: {
  state: CleoChipState
  onClick: () => void
}) {
  const { t } = useTranslation()

  // The draft-in-progress label is the box's own badge rather than a status pill,
  // matching the card it belongs to.
  if (state === 'draft') {
    return (
      <span className={styles.composerCleoChip}>
        <StarOutlined />
        {t('frontend:chatwoot.cleo.draftChip')}
      </span>
    )
  }

  const chip = {
    waiting: {
      label: t('frontend:chatwoot.cleo.showDraft'),
      tooltip: t('frontend:chatwoot.cleo.showDraftHint'),
      icon: <StarOutlined />,
      variant: styles.composerChipAction,
      actionable: true,
    },
    generating: {
      label: t('frontend:chatwoot.cleo.chipDrafting'),
      tooltip: t('frontend:chatwoot.cleo.chipDrafting'),
      icon: <LoadingOutlined />,
      variant: '',
      actionable: false,
    },
    generate: {
      label: t('frontend:chatwoot.cleo.generate'),
      tooltip: t('frontend:chatwoot.cleo.generate'),
      icon: <StarOutlined />,
      variant: styles.composerChipAction,
      actionable: true,
    },
    compose: {
      label: t('frontend:chatwoot.cleo.composeChip'),
      tooltip: t('frontend:chatwoot.cleo.composeChipHint'),
      icon: <StarOutlined />,
      variant: styles.composerChipAction,
      actionable: true,
    },
    noReply: {
      label: t('frontend:chatwoot.cleo.chipNoDraft'),
      // The full explanation, verbatim from where it used to sit on screen.
      tooltip: t('frontend:chatwoot.cleo.noReply'),
      icon: null,
      variant: '',
      actionable: false,
    },
    error: {
      label: t('frontend:chatwoot.cleo.chipFailed'),
      tooltip: t('frontend:chatwoot.cleo.error'),
      icon: <StarOutlined />,
      variant: styles.composerChipError,
      actionable: true,
    },
  }[state]

  const className = [styles.composerChip, chip.variant]
    .filter(Boolean)
    .join(' ')
  const label = <span className={styles.composerChipLabel}>{chip.label}</span>

  return (
    <Tooltip title={chip.tooltip}>
      {chip.actionable ? (
        <button
          type="button"
          className={`${className} ${styles.composerChipButton}`}
          onClick={onClick}
        >
          {chip.icon}
          {label}
        </button>
      ) : (
        // Not a button: there is nothing to click, and offering one would
        // promise a draft Cleo has already declined to write.
        <span className={className}>
          {chip.icon}
          {label}
        </span>
      )}
    </Tooltip>
  )
}
